import { api, http } from '../client';
import type { PageParams } from '@/types/api';
import type { UUID } from '@/types/domain';
import type {
  ApprovalStatus,
  AssignmentStatus,
  AutoAssignResult,
  BillingRunResult,
  CollectionReportRow,
  Discount,
  DiscountKind,
  FeeAssignment,
  FeeCategory,
  FeeStructure,
  FeeSummaryRow,
  FinanceDashboard,
  FinanceEvent,
  FinanceSettings,
  FinanceStatus,
  Invoice,
  InvoiceStatus,
  LateFeeRule,
  LateFeeRunResult,
  OutstandingReportRow,
  Payment,
  PaymentMethod,
  PaymentResult,
  PaymentStatus,
  Receipt,
  Refund,
  RefundStatus,
  ReliefReportRow,
  Scholarship,
  StudentBalance,
  StudentLedgerRow,
} from '@/types/finance';

export interface StatusChangePayload {
  status: FinanceStatus;
  reason?: string;
}

// ------------------------------------------------------------------- settings

export const financeSettingsApi = {
  get: () => api.get<FinanceSettings>('/finance/settings'),
  update: (payload: Omit<FinanceSettings, 'schoolId' | 'updatedAt'>) =>
    api.put<FinanceSettings>('/finance/settings', payload),
};

// ----------------------------------------------------------------- categories

export interface FeeCategoryPayload {
  code: string;
  name: string;
  description?: string;
  refundable: boolean;
  lateFeeApplicable: boolean;
  displayOrder?: number;
}

export const feeCategoriesApi = {
  list: (activeOnly = false) =>
    api.get<FeeCategory[]>('/fee-categories', { params: { activeOnly } }),
  get: (id: UUID) => api.get<FeeCategory>(`/fee-categories/${id}`),
  create: (payload: FeeCategoryPayload) => api.post<FeeCategory>('/fee-categories', payload),
  update: (id: UUID, payload: FeeCategoryPayload) =>
    api.put<FeeCategory>(`/fee-categories/${id}`, payload),
  /** No delete: old invoice lines name their category and must stay readable. */
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<FeeCategory>(`/fee-categories/${id}/status`, payload),
};

// ----------------------------------------------------------------- structures

export interface FeeStructurePayload {
  feeCategoryId: UUID;
  academicYearId: UUID;
  campusId?: UUID;
  gradeOfferingId?: UUID;
  name: string;
  description?: string;
  amount: number;
  frequency: string;
  dueDayOfMonth?: number;
  dueAfterDays?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  mandatory: boolean;
  amountOverridable: boolean;
  taxable: boolean;
  taxPercentage?: number;
}

export const feeStructuresApi = {
  list: (
    params: PageParams & {
      academicYearId?: UUID;
      feeCategoryId?: UUID;
      campusId?: UUID;
      status?: FinanceStatus;
    },
  ) => api.getPage<FeeStructure>('/fee-structures', { params }),
  get: (id: UUID) => api.get<FeeStructure>(`/fee-structures/${id}`),
  create: (payload: FeeStructurePayload) => api.post<FeeStructure>('/fee-structures', payload),
  /** Category, year and scope are fixed after creation. */
  update: (id: UUID, payload: FeeStructurePayload) =>
    api.put<FeeStructure>(`/fee-structures/${id}`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<FeeStructure>(`/fee-structures/${id}/status`, payload),
};

// ---------------------------------------------------------------- assignments

export interface FeeAssignmentPayload {
  enrollmentId: UUID;
  feeStructureId: UUID;
  amount?: number;
  startDate: string;
  endDate?: string;
  notes?: string;
}

export const feeAssignmentsApi = {
  list: (
    params: PageParams & { enrollmentId?: UUID; studentId?: UUID; status?: AssignmentStatus },
  ) => api.getPage<FeeAssignment>('/fee-assignments', { params }),
  forEnrolment: (enrollmentId: UUID) =>
    api.get<FeeAssignment[]>(`/fee-assignments/enrolment/${enrollmentId}`),
  create: (payload: FeeAssignmentPayload) =>
    api.post<FeeAssignment>('/fee-assignments', payload),
  update: (id: UUID, payload: FeeAssignmentPayload) =>
    api.put<FeeAssignment>(`/fee-assignments/${id}`, payload),
  /**
   * Safe to run repeatedly: fees already assigned are counted and skipped, never
   * duplicated.
   */
  autoAssign: (payload: { gradeOfferingId?: UUID; enrollmentId?: UUID; startDate: string }) =>
    api.post<AutoAssignResult>('/fee-assignments/auto-assign', payload),
  changeStatus: (id: UUID, status: AssignmentStatus, endDate?: string) =>
    api.patch<FeeAssignment>(`/fee-assignments/${id}/status`, undefined, {
      params: { status, ...(endDate !== undefined ? { endDate } : {}) },
    }),
};

// ------------------------------------------------------------------- invoices

export interface InvoiceLinePayload {
  feeCategoryId: UUID;
  feeStructureId?: UUID;
  feeAssignmentId?: UUID;
  periodKey?: string;
  description: string;
  unitAmount: number;
  quantity?: number;
  discountAmount?: number;
  taxAmount?: number;
  displayOrder?: number;
}

export interface InvoicePayload {
  enrollmentId: UUID;
  termId?: UUID;
  issueDate: string;
  dueDate: string;
  billingPeriod?: string;
  periodStart?: string;
  periodEnd?: string;
  notes?: string;
  lines: InvoiceLinePayload[];
}

export interface BillingRunPayload {
  academicYearId: UUID;
  gradeOfferingId?: UUID;
  enrollmentId?: UUID;
  periodStart: string;
  issueDate: string;
  issueImmediately?: boolean;
  dryRun?: boolean;
}

export const invoicesApi = {
  list: (
    params: PageParams & {
      studentId?: UUID;
      enrollmentId?: UUID;
      academicYearId?: UUID;
      status?: InvoiceStatus;
      from?: string;
      to?: string;
      outstandingOnly?: boolean;
    },
  ) => api.getPage<Invoice>('/invoices', { params }),

  get: (id: UUID) => api.get<Invoice>(`/invoices/${id}`),

  /** Totals are computed from the lines; amounts sent for them are ignored. */
  create: (payload: InvoicePayload) => api.post<Invoice>('/invoices', payload),

  /** Only a draft may be edited, and its lines are replaced as a set. */
  update: (id: UUID, payload: InvoicePayload) => api.put<Invoice>(`/invoices/${id}`, payload),

  /** Freezes the charges, debits the ledger, records a notification event. */
  issue: (id: UUID) => api.post<Invoice>(`/invoices/${id}/issue`),

  /** Refused once any money has been taken against it. */
  cancel: (id: UUID, reason: string) => api.post<Invoice>(`/invoices/${id}/cancel`, { reason }),

  applyRelief: (id: UUID, params: { discountId?: UUID; scholarshipId?: UUID }) =>
    api.post<{ invoice: Invoice; applied: { name: string; type: string; amount: number }[] }>(
      `/invoices/${id}/relief`,
      undefined,
      { params },
    ),

  writeOff: (id: UUID, payload: { amount: number; reason: string }) =>
    api.post<Invoice>(`/invoices/${id}/write-off`, payload),

  reverseAdjustment: (id: UUID, adjustmentId: UUID, reason: string) =>
    api.post<Invoice>(`/invoices/${id}/adjustments/${adjustmentId}/reverse`, { reason }),

  /** One invoice per enrolment per period. Re-running bills nothing twice. */
  billingRun: (payload: BillingRunPayload) =>
    api.post<BillingRunResult>('/invoices/billing-run', payload),
};

// ------------------------------------------------------------------- payments

export interface RecordPaymentPayload {
  studentId: UUID;
  guardianId?: UUID;
  payerName?: string;
  amount: number;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  referenceNumber?: string;
  bankName?: string;
  notes?: string;
  pending?: boolean;
  autoAllocate?: boolean;
  allocations?: { invoiceId: UUID; amount: number }[];
}

export const paymentsApi = {
  list: (
    params: PageParams & {
      studentId?: UUID;
      status?: PaymentStatus;
      method?: PaymentMethod;
      from?: string;
      to?: string;
      unallocatedOnly?: boolean;
    },
  ) => api.getPage<Payment>('/payments', { params }),

  get: (id: UUID) => api.get<Payment>(`/payments/${id}`),
  balance: (studentId: UUID) => api.get<StudentBalance>(`/payments/balance/${studentId}`),
  receipt: (id: UUID) => api.get<Receipt>(`/payments/${id}/receipt`),

  /** Amounts are capped at each invoice's outstanding balance by the backend. */
  record: (payload: RecordPaymentPayload) => api.post<PaymentResult>('/payments', payload),

  allocate: (
    id: UUID,
    payload: { autoAllocate?: boolean; allocations: { invoiceId: UUID; amount: number }[] },
  ) => api.post<PaymentResult>(`/payments/${id}/allocate`, payload),

  markCleared: (id: UUID) => api.post<Payment>(`/payments/${id}/clear`),

  /** Distinct from a refund: this says the money never arrived. */
  void: (id: UUID, reason: string) => api.post<Payment>(`/payments/${id}/void`, { reason }),
};

// --------------------------------------------------------- discounts & awards

export interface DiscountPayload {
  name: string;
  discountKind: DiscountKind;
  calculation: string;
  value: number;
  maxAmount?: number;
  studentId?: UUID;
  enrollmentId?: UUID;
  reason: string;
  effectiveFrom: string;
  effectiveTo?: string;
  earlyPaymentDays?: number;
  feeCategoryIds?: UUID[];
}

export const discountsApi = {
  list: (
    params: PageParams & { studentId?: UUID; approvalStatus?: ApprovalStatus; kind?: DiscountKind },
  ) => api.getPage<Discount>('/discounts', { params }),
  get: (id: UUID) => api.get<Discount>(`/discounts/${id}`),
  /** Always created pending, whoever proposes it. */
  create: (payload: DiscountPayload) => api.post<Discount>('/discounts', payload),
  update: (id: UUID, payload: DiscountPayload) => api.put<Discount>(`/discounts/${id}`, payload),
  /** A separate permission from proposing one. */
  decide: (id: UUID, payload: { approved: boolean; reason?: string }) =>
    api.post<Discount>(`/discounts/${id}/decision`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<Discount>(`/discounts/${id}/status`, payload),
};

export interface ScholarshipPayload {
  name: string;
  studentId: UUID;
  enrollmentId?: UUID;
  awardType: string;
  value: number;
  maxAmount?: number;
  recurrence: string;
  startDate: string;
  endDate?: string;
  reason: string;
  notes?: string;
  feeCategoryIds?: UUID[];
}

export const scholarshipsApi = {
  list: (params: PageParams & { studentId?: UUID; approvalStatus?: ApprovalStatus }) =>
    api.getPage<Scholarship>('/scholarships', { params }),
  get: (id: UUID) => api.get<Scholarship>(`/scholarships/${id}`),
  create: (payload: ScholarshipPayload) => api.post<Scholarship>('/scholarships', payload),
  update: (id: UUID, payload: ScholarshipPayload) =>
    api.put<Scholarship>(`/scholarships/${id}`, payload),
  decide: (id: UUID, payload: { approved: boolean; reason?: string }) =>
    api.post<Scholarship>(`/scholarships/${id}/decision`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<Scholarship>(`/scholarships/${id}/status`, payload),
};

// ------------------------------------------------------------------ late fees

export interface LateFeeRulePayload {
  name: string;
  description?: string;
  feeCategoryId?: UUID;
  academicYearId?: UUID;
  chargeType: string;
  value: number;
  frequency: string;
  graceDays: number;
  maxAmount?: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export const lateFeesApi = {
  list: () => api.get<LateFeeRule[]>('/late-fee-rules'),
  create: (payload: LateFeeRulePayload) => api.post<LateFeeRule>('/late-fee-rules', payload),
  update: (id: UUID, payload: LateFeeRulePayload) =>
    api.put<LateFeeRule>(`/late-fee-rules/${id}`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<LateFeeRule>(`/late-fee-rules/${id}/status`, payload),
  /** Computes the whole charge owed as at a date and adds only what is missing. */
  apply: (payload: { asOfDate: string; invoiceId?: UUID; dryRun?: boolean }) =>
    api.post<LateFeeRunResult>('/late-fee-rules/apply', payload),
};

// -------------------------------------------------------------------- refunds

export const refundsApi = {
  list: (params: PageParams & { studentId?: UUID; status?: RefundStatus; from?: string; to?: string }) =>
    api.getPage<Refund>('/refunds', { params }),
  get: (id: UUID) => api.get<Refund>(`/refunds/${id}`),
  request: (payload: {
    paymentId: UUID;
    amount: number;
    reason: string;
    refundMethod: PaymentMethod;
    notes?: string;
  }) => api.post<Refund>('/refunds', payload),
  decide: (id: UUID, payload: { approved: boolean; reason?: string }) =>
    api.post<Refund>(`/refunds/${id}/decision`, payload),
  /** The only step that moves money. */
  process: (id: UUID, payload: { refundDate: string; referenceNumber?: string; notes?: string }) =>
    api.post<Refund>(`/refunds/${id}/process`, payload),
  cancel: (id: UUID, reason: string) => api.post<Refund>(`/refunds/${id}/cancel`, { reason }),
};

// ---------------------------------------------------- dashboard, reports, CSV

export const financeReportsApi = {
  dashboard: (params: {
    academicYearId?: UUID;
    gradeOfferingId?: UUID;
    from?: string;
    to?: string;
  }) => api.get<FinanceDashboard>('/finance/dashboard', { params }),

  collection: (params: { from: string; to: string; studentId?: UUID; method?: PaymentMethod }) =>
    api.get<CollectionReportRow[]>('/finance/reports/collection', { params }),

  outstanding: (params: {
    academicYearId?: UUID;
    gradeOfferingId?: UUID;
    studentId?: UUID;
    overdueOnly?: boolean;
  }) => api.get<OutstandingReportRow[]>('/finance/reports/outstanding', { params }),

  summary: (params: { academicYearId?: UUID; from?: string; to?: string; groupBy?: string }) =>
    api.get<FeeSummaryRow[]>('/finance/reports/summary', { params }),

  ledger: (studentId: UUID) =>
    api.get<StudentLedgerRow[]>(`/finance/reports/ledger/${studentId}`),

  relief: (params: { academicYearId?: UUID; from?: string }) =>
    api.get<ReliefReportRow[]>('/finance/reports/relief', { params }),

  events: (params: PageParams) => api.getPage<FinanceEvent>('/finance/events', { params }),

  /**
   * Downloads a report as CSV.
   *
   * <p>Generated by the backend, behind its own permission and taking the same
   * filters as the screen — so an export can never be wider than the table it
   * came from. The browser is handed bytes, not asked to build a file from data
   * it already has.
   */
  async download(path: string, params: Record<string, unknown>, fallbackName: string) {
    const response = await http.get(`/finance/reports/${path}/export`, {
      params,
      responseType: 'blob',
    });

    const disposition = String(response.headers['content-disposition'] ?? '');
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] ?? fallbackName;

    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};
